/**
 * Subscription API Helper
 * Handles all API calls related to subscriptions, plans, and Paymob checkout
 */
import { getToken } from '../utils/authToken';

const API_BASE_URL = 'https://api.mazraaonline.com/api';

/**
 * Make an authenticated API request
 * @param {string} endpoint - The API endpoint (without base URL)
 * @param {Object} options - Fetch options (method, body, etc.)
 * @returns {Promise<Object>} The response data
 */
const apiRequest = async (endpoint, options = {}) => {
  const token = getToken();
  
  if (!token) {
    throw new Error('Please log in to view subscription plans.');
  }

  const url = `${API_BASE_URL}${endpoint}`;
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
    ...options.headers,
  };

  const config = {
    ...options,
    headers,
  };

  try {
    const response = await fetch(url, config);
    const data = await response.json();

    if (!response.ok) {
      // Extract error message from various possible response structures
      const errorMessage = data.message || data.error || data.data?.message || `Request failed with status ${response.status}`;
      throw new Error(errorMessage);
    }

    return data;
  } catch (error) {
    // If it's already an Error with a message, re-throw it
    if (error instanceof Error && error.message) {
      throw error;
    }
    // Handle JSON parse errors or network errors
    if (error.message && error.message.includes('JSON')) {
      throw new Error('Invalid response from server. Please try again.');
    }
    throw new Error('Network error: Failed to connect to the server');
  }
};

/**
 * Fetch the current subscription status for the authenticated user
 * @returns {Promise<Object>} Subscription status object
 */
export const getSubscriptionStatus = async () => {
  const response = await apiRequest('/subscriptions/status', {
    method: 'GET',
  });
  
  if (response.status === 'success' && response.data) {
    return response.data;
  }
  
  throw new Error('Invalid response format from subscription status endpoint');
};

/**
 * Fetch all available subscription plans (for users, not admin)
 * @returns {Promise<Array>} Array of subscription plans
 */
export const getPlans = async () => {
  const response = await apiRequest('/subscriptions/plans', {
    method: 'GET',
  });
  
  if (response.status === 'success' && response.data) {
    return response.data;
  }
  
  throw new Error('Invalid response format from plans endpoint');
};

/**
 * Validate a discount code
 * Matches Postman guide: GET /api/discount-codes/validate/:code
 * @param {string} code - The discount code to validate
 * @param {string} planId - Optional plan ID
 * @param {string} registrationType - Optional registration type
 * @returns {Promise<Object>} Discount code validation result
 * 
 * Response format (matching Postman guide):
 * {
 *   status: "success",
 *   data: {
 *     code: "SAVE20",
 *     discountType: "percentage",
 *     discountValue: 20,
 *     discountAmount: 2000,
 *     isValid: true
 *   }
 * }
 */
export const validateDiscountCode = async (code, planId = null, registrationType = null) => {
  if (!code) {
    throw new Error('Discount code is required');
  }

  const params = new URLSearchParams();
  if (planId) params.append('planId', planId);
  if (registrationType) params.append('registrationType', registrationType);

  const queryString = params.toString();
  const endpoint = `/discount-codes/validate/${code}${queryString ? `?${queryString}` : ''}`;

  // Use apiRequest to include authentication token (matching Postman guide)
  return await apiRequest(endpoint, {
    method: 'GET',
  });
};

/**
 * Create a Paymob checkout session for a specific plan
 * Matches API endpoint: POST /api/subscriptions/checkout
 * @param {string} planId - The ID of the plan to subscribe to
 * @param {string} discountCode - Optional discount code
 * @returns {Promise<Object>} Checkout object with url, orderId, subscriptionId, paymentAmount, discount, etc.
 * 
 * Response format (from actual API):
 * {
 *   status: "success",
 *   data: {
 *     gateway: "paymob",
 *     url: "https://accept.paymob.com/api/acceptance/iframes/...",
 *     orderId: 444465692,
 *     paymentKey: "...",
 *     displayPrice: { amount: 175000, currency: "USD" },
 *     paymentAmount: { amount: 131250, currency: "EGP" },
 *     discount: { code: "SAVE25", type: "percentage", value: 25, amount: 43750 },
 *     originalAmount: 175000,
 *     planId: "693d9a8418e910129ecc16ee",
 *     planName: "Mazraa 50",
 *     subscriptionId: "695689000ed8add6cac6021d"
 *   }
 * }
 */
export const createCheckout = async (planId, discountCode = null) => {
  if (!planId) {
    throw new Error('Plan ID is required');
  }

  // Request body matches Postman guide format
  const body = { 
    planId,
  };

  if (discountCode) {
    body.discountCode = discountCode;
  }

  const response = await apiRequest('/subscriptions/checkout', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  
  // API returns: { status: "success", data: { url: "...", orderId: "...", subscriptionId: "...", paymentAmount: {...}, ... } }
  // Response format from actual API
  if (response.status === 'success' && response.data) {
    const checkoutData = response.data;
    
    // Extract amount and currency from paymentAmount object
    const amount = checkoutData.paymentAmount?.amount || checkoutData.amount;
    const currency = checkoutData.paymentAmount?.currency || checkoutData.currency;
    
    // The API returns 'url' in the data object (Paymob iframe/redirect URL)
    if (checkoutData.url) {
      return {
        url: checkoutData.url,
        orderId: checkoutData.orderId,
        paymentKey: checkoutData.paymentKey,
        amount: amount,
        currency: currency,
        displayPrice: checkoutData.displayPrice,
        paymentAmount: checkoutData.paymentAmount,
        discount: checkoutData.discount,
        originalAmount: checkoutData.originalAmount,
        planName: checkoutData.planName,
        subscriptionId: checkoutData.subscriptionId,
        planId: checkoutData.planId || planId,
        gateway: checkoutData.gateway || 'paymob',
      };
    }
    
    // Fallback: check for other possible field names
    if (checkoutData.iframeUrl || checkoutData.iframe_url) {
      return {
        url: checkoutData.iframeUrl || checkoutData.iframe_url,
        orderId: checkoutData.orderId,
        paymentKey: checkoutData.paymentKey,
        amount: amount,
        currency: currency,
        displayPrice: checkoutData.displayPrice,
        paymentAmount: checkoutData.paymentAmount,
        discount: checkoutData.discount,
        originalAmount: checkoutData.originalAmount,
        planName: checkoutData.planName,
        subscriptionId: checkoutData.subscriptionId,
        planId: checkoutData.planId || planId,
        gateway: checkoutData.gateway || 'paymob',
      };
    }
    
    // If we have data but no URL, log it for debugging
    console.warn('Checkout response received but no URL found:', checkoutData);
    throw new Error('Checkout response received but payment URL is missing');
  }
  
  throw new Error(response.message || 'Invalid response format from checkout endpoint');
};

/**
 * Get current subscription for the authenticated user
 * Matches Postman guide: GET /api/subscriptions/current
 * @returns {Promise<Object>} Current subscription object with status, plan, paymobOrderId, etc.
 */
export const getCurrentSubscription = async () => {
  const response = await apiRequest('/subscriptions/current', {
    method: 'GET',
  });
  
  if (response.status === 'success' && response.data) {
    return response.data;
  }
  
  throw new Error(response.message || 'Invalid response format from current subscription endpoint');
};

